import { CLoginPopup, FAMILY_STEPS } from "./LoginPopup/CLoginPopup";
import { LoginContext } from "./LoginPopup/types/LoginContext";

export default class LoginTriggerManager {
  /** The side the host serves, as the popup's markup says; null on a host that serves both */
  private readonly defaultContext: LoginContext | null;

  constructor(private readonly popup: CLoginPopup = new CLoginPopup()) {
    const context = popup.currentTarget.attr("data-default-context") ?? "";
    const contexts: string[] = Object.values(LoginContext);
    this.defaultContext = contexts.includes(context)
      ? (context as LoginContext)
      : null;
    this.initializeTriggers();
    this.handleInitialPageState();
  }

  // Login opening methods
  public OpenSchoolLogin(): void {
    this.popup.Reset();
    this.popup.SetContext(LoginContext.SCHOOL, ["userrole", "login"]);
    this.popup.Show();
  }

  public OpenFamilyLogin(): void {
    this.popup.Reset();
    this.popup.SetContext(LoginContext.FAMILY, FAMILY_STEPS);
    this.popup.Show();
  }

  public OpenContextualLogin(): void {
    this.popup.Reset();
    this.popup.SetContext(LoginContext.SCHOOL, ["platform"]);
    this.popup.Show();
  }

  public OpenCreateAccount(context: LoginContext | null): void {
    if (context === null) {
      context = LoginContext.SCHOOL;
    }
    this.popup.Reset();
    // A family account starts from its email; the school side asks who the visitor is
    this.popup.SetContext(
      context,
      context === LoginContext.FAMILY ? FAMILY_STEPS : ["accountcreation"],
    );
    this.popup.Show();
  }
  public OpenCustomLogin(
    context: LoginContext = LoginContext.SCHOOL,
    steps: string[] = ["userrole", "login"],
    userType: "TEACHER" | "STUDENT" | "CHILD" | "PARENT" = null,
  ): void {
    this.popup.Reset();
    this.popup.SetContext(context, steps);
    if (userType) {
      this.popup.UpdateUserType(userType);
    }
    this.popup.Show();
  }

  // Initialize all login triggers on the page
  private initializeTriggers(): void {
    this.bindSchoolLoginTriggers();
    this.bindFamilyLoginTriggers();
    this.bindCatalogLoginTriggers();
    this.bindGenericTriggers();
  }

  private bindCatalogLoginTriggers(): void {
    // The login button inside a challenge card: the family host knows who is
    // visiting, the school host asks which platform first
    $(".js-template-login-button").on("click", () => {
      if (this.defaultContext === LoginContext.FAMILY) {
        this.OpenFamilyLogin();
      } else {
        this.OpenContextualLogin();
      }
    });
  }

  private bindSchoolLoginTriggers(): void {
    // School login buttons on landing pages
    $(".js-open-school-login").on("click", (event) => {
      event.preventDefault();
      this.OpenSchoolLogin();
    });
  }

  private bindFamilyLoginTriggers(): void {
    // Family login buttons on landing pages
    $(".js-open-family-login").on("click", (event) => {
      event.preventDefault();
      this.OpenFamilyLogin();
    });
  }

  private bindGenericTriggers(): void {
    $(".open-login-popup").on("click", (event) => {
      event.preventDefault();

      switch (this.defaultContext) {
        case LoginContext.FAMILY:
          this.OpenFamilyLogin();
          break;
        case LoginContext.SCHOOL:
          this.OpenSchoolLogin();
          break;
        default:
          this.OpenContextualLogin();
          break;
      }
    });

    $(".js-create-account-link").on("click", (event) => {
      event.preventDefault();
      this.OpenCreateAccount(this.defaultContext);
    });
  }

  // Page state detection and handling
  private handleInitialPageState(): void {
    if (this.shouldOpenOnPageLoad()) {
      this.openOnPageLoad();
    }
  }

  private shouldOpenOnPageLoad(): boolean {
    const url = new URL(window.location.href);
    const hasOpenParam = url.searchParams.get("open-login-popup");
    const hasValidationErrors = this.popup.HasValidationErrors();
    const hasOpenDataAttribute = Boolean(
      $("form[name='form-signin-popup']").data("open-on-load"),
    );

    return !!(hasOpenParam || hasValidationErrors || hasOpenDataAttribute);
  }

  private openOnPageLoad(): void {
    // A failed login reopens the form it was tried on, whatever the page's context
    if (this.popup.HasValidationErrors()) {
      if (this.popup.FailedOnFamilyGate()) {
        this.OpenFamilyLogin();
      } else {
        this.OpenSchoolLogin();
        this.popup.UpdateUserType("TEACHER");
        this.popup.GoToStep("login");
      }
      return;
    }
    // A host that serves both sides (the admin one, where a teacher's session expires) asks which
    switch (this.defaultContext) {
      case LoginContext.SCHOOL:
        this.OpenSchoolLogin();
        this.popup.UpdateUserType("TEACHER");
        break;
      case LoginContext.FAMILY:
        this.OpenFamilyLogin();
        break;
      default:
        this.OpenContextualLogin();
        break;
    }
  }
}
